import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense } from 'react';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { JobsProvider } from '@/context/JobsContext';
import SessionTimeoutBanner from '@/components/SessionTimeoutBanner';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import '@/App.css';

// Layouts — kept eagerly imported because every authenticated screen
// renders through them; lazy-loading them would just add a Suspense flash
// on every navigation inside the dashboard.
import PublicLayout from '@/layouts/PublicLayout';
import DashboardLayout from '@/layouts/DashboardLayout';

// ─── Lazy-loaded page modules ──────────────────────────────────────────
// Each page is fetched on first navigation only, then cached by the
// browser for the rest of the session. This keeps the initial JS bundle
// small (no admin/appraiser code for professionals, no Recharts for
// non-admins, etc.) without adding any new dependencies.

// Public Pages
const Home              = lazy(() => import('@/pages/public/Home'));
const ForProfessionals  = lazy(() => import('@/pages/public/ForProfessionals'));
const ForAppraisers     = lazy(() => import('@/pages/public/ForAppraisers'));
const Pricing           = lazy(() => import('@/pages/public/Pricing'));
const Login             = lazy(() => import('@/pages/public/Login'));
const ResetPassword     = lazy(() => import('@/pages/public/ResetPassword'));
const ReactivateAccount = lazy(() => import('@/pages/public/ReactivateAccount'));
const SignUp            = lazy(() => import('@/pages/public/SignUp'));
const SignUpSuccess     = lazy(() => import('@/pages/public/SignUpSuccess'));
const Contact           = lazy(() => import('@/pages/public/Contact'));
const HowItWorks        = lazy(() => import('@/pages/public/HowItWorks'));
const About             = lazy(() => import('@/pages/public/About'));
const TermsOfService    = lazy(() => import('@/pages/public/TermsOfService'));
const PrivacyPolicy     = lazy(() => import('@/pages/public/PrivacyPolicy'));
const ProVideo          = lazy(() => import('@/pages/public/ProVideo'));
const JobPrintView      = lazy(() => import('@/pages/JobPrintView'));

// Professional Pages
const ProfessionalDashboard = lazy(() => import('@/pages/professional/Dashboard'));
const CreateJob             = lazy(() => import('@/pages/professional/CreateJob'));
const ViewJob               = lazy(() => import('@/pages/professional/ViewJob'));
const AppraiserDirectory    = lazy(() => import('@/pages/professional/AppraiserDirectory'));
const AppraiserProfile      = lazy(() => import('@/pages/professional/AppraiserProfile'));
const ProfessionalMyProfile = lazy(() => import('@/pages/professional/MyProfile'));
const ProfessionalSettings  = lazy(() => import('@/pages/professional/Settings'));
const ProfessionalBilling   = lazy(() => import('@/pages/professional/Billing'));

// Appraiser Pages
const AppraiserDashboard = lazy(() => import('@/pages/appraiser/Dashboard'));
const AppraiserViewJob   = lazy(() => import('@/pages/appraiser/ViewJob'));
const Invitations        = lazy(() => import('@/pages/appraiser/Invitations'));
const MyProfile          = lazy(() => import('@/pages/appraiser/MyProfile'));
const AppraiserSettings  = lazy(() => import('@/pages/appraiser/Settings'));

// Admin Pages — these are the heaviest (Recharts ≈ 200 KB) so lazy-loading
// here yields the biggest reduction in initial bundle for non-admin users.
const AdminDashboard              = lazy(() => import('@/pages/admin/Dashboard'));
const AdminAllJobs                = lazy(() => import('@/pages/admin/AllJobs'));
const AdminAppraiserDirectory     = lazy(() => import('@/pages/admin/AppraiserDirectory'));
const AdminProfessionalDirectory  = lazy(() => import('@/pages/admin/ProfessionalDirectory'));
const AdminEmailNotifications     = lazy(() => import('@/pages/admin/EmailNotifications'));
const AdminSettings               = lazy(() => import('@/pages/admin/Settings'));
const AdminViewJob                = lazy(() => import('@/pages/admin/ViewJob'));
const AdminContactSubmissions     = lazy(() => import('@/pages/admin/ContactSubmissions'));
const AdminCoupons                = lazy(() => import('@/pages/admin/Coupons'));
const AdminAccountCoupons         = lazy(() => import('@/pages/admin/AccountCoupons'));
const Help                        = lazy(() => import('@/pages/shared/Help'));

/** Suspense fallback for lazy-loaded routes.
 *
 * Returns `null` instead of a spinner because:
 *   1. Most navigation hits a chunk that's already prefetched by
 *      `lib/routePrefetch.js` — Suspense fires for <50 ms and a spinner
 *      would just be a visual flash.
 *   2. Pages that DO have their own data-loading state (Dashboard, ViewJob,
 *      AppraiserDirectory, etc.) render their own spinner in a stable
 *      position once mounted. Showing a separate Suspense spinner at a
 *      DIFFERENT position causes a visible jump — the bug the user filed.
 *   3. For the rare first-ever visit to a page with no prefetch, a brief
 *      blank flash (<150 ms) is less jarring than a positioned spinner
 *      that then "moves" when the real page mounts.
 */
function RouteFallback() {
  return null;
}

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading, refreshUser, logout } = useAuth();
  const [pendingChecked, setPendingChecked] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [resumingPayment, setResumingPayment] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');

  const resumeSubscriptionCheckout = async () => {
    if (resumingPayment) return;
    setRecoveryError('');
    setResumingPayment(true);
    try {
      const response = await api.post('/api/payments/create-subscription-checkout', {
        user_id: user.user_id,
        email: user.email,
        origin_url: window.location.origin,
      });
      window.location.assign(response.data.url);
    } catch (error) {
      setRecoveryError(
        error.response?.data?.detail
        || 'We could not restart checkout. Please try again.',
      );
      setResumingPayment(false);
    }
  };

  const backToLogin = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed', error);
    } finally {
      window.location.replace('/login');
    }
  };

  // If the user is professional+pending, try one silent refresh before showing the
  // "Subscription Required" gate. This handles the post-Stripe redirect race where
  // the in-memory user object is stale even though the DB is already 'active'.
  useEffect(() => {
    if (
      user?.role === 'professional' &&
      user?.subscription_status === 'pending' &&
      !pendingChecked &&
      !refreshing
    ) {
      setRefreshing(true);
      refreshUser().finally(() => {
        setPendingChecked(true);
        setRefreshing(false);
      });
    } else if (user && user.subscription_status !== 'pending' && !pendingChecked) {
      setPendingChecked(true);
    }
  }, [user, pendingChecked, refreshing, refreshUser]);

  if (loading) {
    // Check localStorage for any saved dark mode preference to avoid flash
    const savedTheme = Object.keys(localStorage).find(k => k.startsWith('theme_'));
    const isDark = savedTheme ? localStorage.getItem(savedTheme) === 'dark' : false;
    return (
      <div className={`flex items-center justify-center h-screen ${isDark ? 'bg-[#101014]' : 'bg-slate-100'}`}>
        <div className={`animate-spin rounded-full h-8 w-8 border-2 border-t-transparent ${isDark ? 'border-white' : 'border-slate-900'}`} />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const dashPath = user.role === 'admin' ? '/admin' : user.role === 'appraiser' ? '/appraiser' : '/professional';
    return <Navigate to={dashPath} replace />;
  }

  // Block professionals with pending subscription — but only after the silent
  // refresh has run, so we don't flash the gate during the post-payment redirect.
  if (user.role === 'professional' && user.subscription_status === 'pending') {
    if (!pendingChecked || refreshing) {
      return (
        <div className="flex items-center justify-center h-screen bg-slate-100">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-900 border-t-transparent" />
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center h-screen bg-slate-100">
        <div className="text-center max-w-md px-6">
          <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
            <svg className="w-7 h-7 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01" /></svg>
          </div>
          <h1 className="font-display text-xl font-bold text-slate-900 mb-2">Subscription Required</h1>
          <p className="text-sm text-slate-500 mb-6">Your professional subscription payment is still pending. Complete payment to access your account, or cancel to start over with a different email.</p>
          <div className="flex flex-col gap-3">
            <Button
              type="button"
              data-testid="resume-subscription-checkout-btn"
              disabled={resumingPayment || signingOut || cancelling}
              onClick={resumeSubscriptionCheckout}
              className="w-full rounded-sm bg-slate-900 text-white hover:bg-slate-800"
            >
              {resumingPayment ? 'Opening Checkout…' : 'Complete Payment'}
            </Button>
            {recoveryError && (
              <p role="alert" className="text-xs text-rose-600">{recoveryError}</p>
            )}
            <button
              type="button"
              data-testid="back-to-login-link"
              disabled={resumingPayment || signingOut || cancelling}
              onClick={backToLogin}
              className="text-sm text-navy-600 font-semibold hover:underline disabled:opacity-60"
            >
              {signingOut ? 'Signing out…' : 'Back to Login'}
            </button>
            <button
              type="button"
              data-testid="cancel-pending-signup-btn"
              disabled={resumingPayment || signingOut || cancelling}
              onClick={async () => {
                if (!window.confirm('This will permanently delete your pending account so you can sign up again. Continue?')) return;
                setCancelling(true);
                try { await api.post('/api/auth/cancel-pending-signup'); } catch (e) { console.error('Cancel pending signup failed', e); }
                try { await logout(); } catch (e) { console.error('Logout failed', e); }
                window.location.href = '/signup?role=professional';
              }}
              className="text-xs text-rose-600 hover:underline disabled:opacity-60"
            >
              {cancelling ? 'Cancelling…' : 'Cancel signup and start over'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public Routes */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/for-professionals" element={<ForProfessionals />} />
          <Route path="/for-appraisers" element={<ForAppraisers />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/signup/appraiser" element={<SignUp />} />
          <Route path="/signup/professional" element={<SignUp />} />
          <Route path="/signup/success" element={<SignUpSuccess />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/reactivate-account" element={<ReactivateAccount />} />
          <Route path="/contact" element={<Contact />} />
          {/* Not linked from the site: shared by email and listed in sitemap.xml. */}
          <Route path="/pro-video" element={<ProVideo />} />
        </Route>

        {/* Professional Routes */}
        <Route
          path="/professional"
          element={
            <ProtectedRoute allowedRoles={['professional']}>
              <DashboardLayout role="professional" />
            </ProtectedRoute>
          }
        >
          <Route index element={<ProfessionalDashboard />} />
          <Route path="jobs/create" element={<CreateJob />} />
          <Route path="jobs/:jobId" element={<ViewJob />} />
          <Route path="appraisers" element={<AppraiserDirectory />} />
          <Route path="appraisers/:userId" element={<AppraiserProfile />} />
          <Route path="profile" element={<ProfessionalMyProfile />} />
          <Route path="billing" element={<ProfessionalBilling />} />
          <Route path="settings" element={<ProfessionalSettings />} />
          <Route path="help" element={<Help />} />
        </Route>

        {/* Appraiser Routes */}
        <Route
          path="/appraiser"
          element={
            <ProtectedRoute allowedRoles={['appraiser']}>
              <DashboardLayout role="appraiser" />
            </ProtectedRoute>
          }
        >
          <Route index element={<AppraiserDashboard />} />
          <Route path="jobs/:jobId" element={<AppraiserViewJob />} />
          <Route path="invitations" element={<Invitations />} />
          <Route path="profile" element={<MyProfile />} />
          <Route path="settings" element={<AppraiserSettings />} />
          <Route path="help" element={<Help />} />
        </Route>

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <DashboardLayout role="admin" />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="jobs" element={<AdminAllJobs />} />
          <Route path="jobs/:jobId" element={<AdminViewJob />} />
          <Route path="appraisers" element={<AdminAppraiserDirectory />} />
          <Route path="professionals" element={<AdminProfessionalDirectory />} />
          <Route path="emails" element={<AdminEmailNotifications />} />
          <Route path="contact" element={<AdminContactSubmissions />} />
          <Route path="coupons" element={<AdminCoupons />} />
          <Route path="account-coupons" element={<AdminAccountCoupons />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>

        {/* Print views — accessible to any logged-in role with access to the job */}
        <Route path="/print/jobs/:jobId" element={
          <ProtectedRoute allowedRoles={['professional', 'appraiser', 'admin']}>
            <JobPrintView />
          </ProtectedRoute>
        } />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <JobsProvider>
            <AppRoutes />
            <SessionTimeoutBanner />
          </JobsProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
